import { LessonPlayer } from "./WebRtcPlayer";

const enum UplinkState {
  INACTIVE,
  GETTING_MEDIA_DEVICES,
  ADDING_RTC_TRACKS,
  ACTIVE,
  IDLE,
}

const makeDeferred = () => {
  const deferred = {} as any;
  deferred.promise = new Promise((resolve, reject) => {
    deferred.resolve = resolve;
    deferred.reject = reject;
  });
  return deferred;
};

export interface Uplink {
  start(): Promise<boolean>;
  startCapturingMedia(answerCaptureId: string): void;
  stopCapturingMedia(): void;
  stop(): void;

  rtcSetRemoteDescriptionSucceeded(): void;
  rtcSetRemoteDescriptionFailed(): void;
}

export class AnswerCaptureAudioOnlyUplink implements Uplink {
  state: UplinkState = UplinkState.INACTIVE;
  currentAnswerCaptureId: string = "";
  didGetUserMediaTimeout: boolean = false;
  mediaStream: MediaStream | null = null;
  lessonPlayer: LessonPlayer;
  onGetCurrentRtcConnection: () => RTCPeerConnection | null | undefined;

  constructor(
    lessonPlayer: LessonPlayer,
    onGetCurrentRtcConnection: () => RTCPeerConnection | null | undefined
  ) {
    this.lessonPlayer = lessonPlayer;
    this.onGetCurrentRtcConnection = onGetCurrentRtcConnection;
  }

  start(): Promise<boolean> {
    // No-op for regular uplink
    return Promise.resolve(true);
  }

  startCapturingMedia(answerCaptureId: string): void {
    // If we are all connected but have "muted" our audio track, then simply reenable it
    if (this.state === UplinkState.IDLE) {
      if (this.resumeFromIdle()) {
        this.currentAnswerCaptureId = answerCaptureId;
        this.lessonPlayer.onAnswerCaptureStarted();
        this.state = UplinkState.ACTIVE;
        return;
      } else {
        // We were not able to resume, so clean up and start from scratch
        this.stopTracks();
        this.removeMediaStreamTracksFromRtcStream();
        this.state = UplinkState.INACTIVE;
        // Continue with start() as if nothing happened
      }
    }

    // If it's the same answer cap id for whatever reason, then this is a no-op.
    // If it's a different cap id but the stream is happening, then
    // store the new id and don't do anything else
    if (this.state !== UplinkState.INACTIVE) {
      this.currentAnswerCaptureId = answerCaptureId;
      return;
    }

    this.state = UplinkState.GETTING_MEDIA_DEVICES;
    this.currentAnswerCaptureId = answerCaptureId;

    navigator.mediaDevices
      .getUserMedia({
        audio: true, // must have it for speech processing
      })
      .then((stream: MediaStream) => {
        // By the time we got here, the lesson could have moved on or stopped
        // Only proceed if we still need to capture for same answer
        if (
          this.state === UplinkState.GETTING_MEDIA_DEVICES &&
          this.currentAnswerCaptureId === answerCaptureId
        ) {
          if (this.requestAddMediaStreamTracksToRtcStream(stream)) {
            this.lessonPlayer.onShowListeningUI?.(stream);
          }
        } else {
          this.state = UplinkState.INACTIVE;
          // We don't need these tracks anymore, so remove them.
          stream.getTracks().forEach((track: MediaStreamTrack) => {
            track.stop();
            stream.removeTrack(track);
          });
        }
      })
      .catch((err) => {
        // User might not have given permission for the input devices to be used
        // or something else failed along the way. Either way, we can't proceed.
        if (
          this.state === UplinkState.GETTING_MEDIA_DEVICES &&
          this.currentAnswerCaptureId === answerCaptureId
        ) {
          this.lessonPlayer.onAnswerCaptureDidNotStart?.();
          this.state = UplinkState.INACTIVE;
        }
      });
  }

  resumeFromIdle(): boolean {
    // If any one of the tracks is dead, we can't resume
    const dead = this.mediaStream
      ?.getTracks()
      .find((track: MediaStreamTrack) => {
        return track.readyState === "ended";
      });

    if (!dead) {
      this.enableMediaStreamTracks();
      this.lessonPlayer.onShowListeningUI?.(this.mediaStream!);
      return true;
    } else {
      return false;
    }
  }

  requestAddMediaStreamTracksToRtcStream(stream: MediaStream): boolean {
    if (this.addMediaStreamTracksToRtcStream(stream)) {
      this.mediaStream = stream;
      this.state = UplinkState.ADDING_RTC_TRACKS;
      return true;
    } else {
      this.state = UplinkState.INACTIVE;
      // We don't need these tracks anymore, so remove them.
      stream.getTracks().forEach((track: MediaStreamTrack) => {
        track.stop();
        stream.removeTrack(track);
      });
      return false;
    }
  }

  stop(): void {
    this.currentAnswerCaptureId = "";

    switch (this.state) {
      case UplinkState.GETTING_MEDIA_DEVICES:
        // Object will clean itself up after the get media devices promise is
        // resolved one way or another. This noop is for documentation purposes only.
        break;

      case UplinkState.ADDING_RTC_TRACKS:
        this.lessonPlayer.onHideListeningUI();
        this.stopTracks();
        break;

      case UplinkState.ACTIVE:
      case UplinkState.IDLE:
        this.lessonPlayer.onHideListeningUI();
        this.stopTracks();
        this.removeMediaStreamTracksFromRtcStream();
        break;
    }

    this.currentAnswerCaptureId = "";
    this.state = UplinkState.INACTIVE;
  }

  stopCapturingMedia(): void {
    if (this.state === UplinkState.ACTIVE) {
      this.lessonPlayer.onHideListeningUI();
      this.disableMediaStreamTracks();
      this.state = UplinkState.IDLE;
    }
  }

  enableMediaStreamTracks(): void {
    this.mediaStream?.getTracks().forEach((track: MediaStreamTrack) => {
      track.enabled = true;
    });
  }

  // This turns off the microphone capture icon in browser
  disableMediaStreamTracks(): void {
    this.mediaStream?.getTracks().forEach((track: MediaStreamTrack) => {
      track.enabled = false;
    });
  }

  // Disassociate underlying resource from this track
  // This turns off media capture icons and terminates the tracks
  stopTracks(): void {
    this.mediaStream?.getTracks().forEach((track: MediaStreamTrack) => {
      track.stop();
    });
  }

  addMediaStreamTracksToRtcStream(mediaStream: MediaStream): boolean {
    const pc = this.onGetCurrentRtcConnection();
    if (pc) {
      mediaStream.getTracks().forEach((track) => {
        pc.addTrack(track, mediaStream);
      });
      return true;
    } else {
      return false;
    }
  }

  removeMediaStreamTracksFromRtcStream(): void {
    // Remove all tracks from the stream
    // This stops audio uplink to the server
    const pc = this.onGetCurrentRtcConnection();
    if (pc && pc.signalingState !== "closed") {
      pc.getSenders().forEach((sender) => pc.removeTrack(sender));
    } else {
    }
  }

  rtcSetRemoteDescriptionSucceeded(): void {
    // Assuming that this is THE answer to our offer to add uplink tracks
    if (this.state === UplinkState.ADDING_RTC_TRACKS) {
      this.state = UplinkState.ACTIVE;
      this.lessonPlayer.onAnswerCaptureStarted?.();
    }
  }

  rtcSetRemoteDescriptionFailed(): void {
    // Assuming that this is THE answer to our offer to add uplink tracks
    if (this.state === UplinkState.ADDING_RTC_TRACKS) {
      this.stop();
      this.lessonPlayer.onAnswerCaptureDidNotStart?.();
    }
  }
}

/*
Uplink object that enables audio/video recording of lesson playback.

Differences from the regular uplink:
- Captures both audio and video from user's microphone and camera
- Starts media capture from the start of the session, not just during answer capture
- "idle" state is a no-op - it's always active until it's fully stopped
- Nothing special happens when answer capture begins
*/
export class AlwaysOnAudioVideoUplink extends AnswerCaptureAudioOnlyUplink {
  deferredSetRemoteDescriptionResult = makeDeferred();

  async start(): Promise<boolean> {
    this.state = UplinkState.GETTING_MEDIA_DEVICES;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        // must have it for speech processing
        audio: true,
        video: {
          width: 640,
          height: 360,
          frameRate: 30,
        }, // to record video of the student taking the lesson
      });

      // By the time we get here, playback could have been stopped
      // Only proceed if this uplink is still required
      if (this.state !== UplinkState.GETTING_MEDIA_DEVICES) {
        this.state = UplinkState.INACTIVE;
        // We don't need these tracks anymore, so remove them.
        stream.getTracks().forEach((track: MediaStreamTrack) => {
          track.stop();
          stream.removeTrack(track);
        });
        return false;
      }

      // Attempt to add the media stream to the rtc connection
      if (!this.requestAddMediaStreamTracksToRtcStream(stream)) {
        this.lessonPlayer.onAudioVideoCaptureDidNotStart?.();
        return false;
      }

      // Wait until we know whether tracks have been added
      // rtcSetRemoteDescriptionSucceeded/Failed will resolve the promise accordingly.
      this.deferredSetRemoteDescriptionResult = makeDeferred();
      return await this.deferredSetRemoteDescriptionResult.promise;
    } catch (err) {
      // User might not have given permission for the input devices to be used
      // or something else failed along the way. Either way, we can't proceed.
      if (this.state === UplinkState.GETTING_MEDIA_DEVICES) {
        this.lessonPlayer.onAudioVideoCaptureDidNotStart?.();
        this.state = UplinkState.INACTIVE;
      }
      return false;
    }
  }

  startCapturingMedia(answerCaptureId: string): void {
    // We are always connected, so nothing special to do here
    if (this.state === UplinkState.IDLE || this.state === UplinkState.ACTIVE) {
      this.currentAnswerCaptureId = answerCaptureId;
      this.lessonPlayer.onShowListeningUI?.(this.mediaStream!);
      this.lessonPlayer.onAnswerCaptureStarted();
      this.state = UplinkState.ACTIVE;
    }
  }

  stopCapturingMedia(): void {
    if (this.state === UplinkState.ACTIVE) {
      this.lessonPlayer.onHideListeningUI();
      this.state = UplinkState.IDLE;
    }
  }

  rtcSetRemoteDescriptionSucceeded(): void {
    // Assuming that this is THE answer to our offer to add uplink tracks
    if (this.state === UplinkState.ADDING_RTC_TRACKS) {
      this.state = UplinkState.ACTIVE;
      this.deferredSetRemoteDescriptionResult.resolve(true);
    }
  }

  rtcSetRemoteDescriptionFailed(): void {
    // Assuming that this is THE answer to our offer to add uplink tracks
    if (this.state === UplinkState.ADDING_RTC_TRACKS) {
      this.stop();
      this.lessonPlayer.onAudioVideoCaptureDidNotStart?.();
      this.deferredSetRemoteDescriptionResult.promise.resolve(false);
    }
  }
}
